import { computed }                       from 'mobx';
import { makeAutoObservable, observable } from 'mobx';
import { PagedCollection }                from 'collections/PagedCollection';
import { ApiCollection }                  from 'collections/ApiCollection';
import StandDocumentStatusModel           from 'models/StandDocumentStatusModel';
import ShowModel                          from 'models/ShowModel';
import StandModel                         from 'models/StandModel';
import currentFilters                     from 'utils/currentFilters';
import currentSession                     from 'utils/currentSession';

const ITEMS_PER_PAGE = 50;

export default class StandDocumentListStore {
  @observable
  public rawFilters: Record<string, any> = {};

  @observable
  public sorts: Record<string, any> = {};

  @observable public show = new ShowModel();
  @observable public standCollection = new PagedCollection(StandModel);
  @observable public standCollectionFull = new ApiCollection(StandModel);
  @observable public standDocumentStatusCollection = new ApiCollection(StandDocumentStatusModel);

  constructor() {
    makeAutoObservable(this);
  }

  public clear = () => {
    this.show.clear();
    this.standCollection.clear();
    this.standCollectionFull.clear();
  };

  public fetchStandDocumentsData = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: Record<string, any>,
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    this.standCollection.itemsPerPage = pagination.pageSize || ITEMS_PER_PAGE;
    this.standCollection.setPage(pagination.current || 1);

    // remove all filters
    this.standCollection.filters = {};

    // set filters
    Object.keys(apiFilters).forEach(filterName => {
      this.standCollection.setFilter(filterName, apiFilters[filterName]);
    });

    this.standCollection.setFilter('show', showId);

    // remove all sorts
    this.standCollection.sorts = {};

    // set sort
    Object.keys(sorter).forEach(sortName => {
      this.standCollection.setSort(sortName, sorter[sortName] === 'asc');
    });

    this.standCollectionFull.setFilters({
      properties: [
        'aisle',
        'hall',
        'id',
        'number',
        'standDocumentStatus',
        'unSeenDocuments',
        'documentsTreated',
      ],
      show: showId,
    });

    await Promise.all([
      this.standCollection
        .list(),

      this.standCollectionFull
        .list(),
    ]);
  }

  public initFromLocalStorage = async () => {
    if (!currentFilters.isLoaded) {
      await currentFilters.load(currentSession.user.user_id);
    }
    this.rawFilters = currentFilters.getFiltersForPath(window.location.pathname);
    this.sorts = currentFilters.getSortsForPath(window.location.pathname);
  }

  public load = async (
    showId: id,
    pagination: { current?: number, pageSize?: number },
    apiFilters: Record<string, any>,
    sorter: Record<string, 'asc' | 'desc'>,
  ) => {
    await Promise.all([
      this.fetchStandDocumentsData(showId, pagination, apiFilters, sorter),

      this.show
        .setId(showId)
        .fetch(),

      this.standDocumentStatusCollection
        .list(),
    ]);
  };

  public setAndSaveRawFilters = async (filters: Record<string, any>, sorts?: any) => {
    this.rawFilters = filters;
    if (sorts) this.sorts = sorts;

    await currentFilters.saveFiltersAndSorts(
      window.location.pathname,
      this.rawFilters,
      this.sorts
    );
  }

  @computed
  public get apiFilters() {
    return this._processFilters(this.rawFilters);
  }

  private _processFilters = (filters: Record<string, any>): StandModel['filters'] => {
    const processed = { ...filters };

    if (processed.surface) {
      const surfaceValues = Array.isArray(processed.surface) ? processed.surface : [processed.surface];
      let min: number | undefined;
      let max: number | undefined;

      surfaceValues.forEach((value: string) => {
        if (typeof value === 'string' && value.includes('_to_')) {
          const [vMin, vMax] = value.split('_to_').map(v => v ? parseInt(v, 10) : undefined);

          if (vMin !== undefined && (min === undefined || vMin < min)) {
            min = vMin;
          }
          if (vMax !== undefined && (max === undefined || vMax > max)) {
            max = vMax;
          } else if (value.endsWith('_to_')) {
            // Cas particulier pour "+50" (51_to_) : on ne veut plus de borne max
            max = undefined;
          }
        }
      });

      processed['surface[gte]'] = min;
      processed['surface[lte]'] = max;

      delete processed.surface;
    }

    return processed;
  };
}
